import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Users, Trophy, Clock, AlertTriangle, UserPlus, CheckCircle, XCircle, X } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { cn } from '../utils/cn';
import { isTournamentCompleted, getTournamentWinner, processAutomaticAdvancement } from '../utils/tournamentUtils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Alert, AlertDescription } from './ui/alert';

const TournamentDetails = ({ session, supabase }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [matches, setMatches] = useState([]);
  const [players, setPlayers] = useState([]);
  const [playerMap, setPlayerMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activatingMatch, setActivatingMatch] = useState(null);
  const [showMatchForm, setShowMatchForm] = useState(false);
  const [matchFormData, setMatchFormData] = useState({
    player1Score: '',
    player2Score: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [isPwa, setIsPwa] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [signedUp, setSignedUp] = useState(false);
  const [showSignupDialog, setShowSignupDialog] = useState(false);
  const [allPlayers, setAllPlayers] = useState([]);
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [showMatchupManager, setShowMatchupManager] = useState(false);
  const [selectedPlayersForMatches, setSelectedPlayersForMatches] = useState({});
  const [matchPairs, setMatchPairs] = useState([]);

  useEffect(() => {
    // Check if running as PWA
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true) {
      setIsPwa(true);
    }

    // Fetch tournament data
    const fetchTournamentData = async () => {
      setLoading(true);
      try {
        // Fetch tournament details
        const { data: tournamentData, error: tournamentError } = await supabase
          .from('tournaments')
          .select('*')
          .eq('id', id)
          .single();

        if (tournamentError) throw tournamentError;
        setTournament(tournamentData);
        
        // Check if registration is open
        setRegistrationOpen(tournamentData.status === 'upcoming');

        // Check if user is admin (tournament creator)
        if (session && tournamentData.created_by === session.user.id) {
          setIsAdmin(true);
        }

        // Fetch tournament players
        const { data: tournamentPlayersData, error: playersError } = await supabase
          .from('tournament_players')
          .select('*, player:player_id(*)')
          .eq('tournament_id', id);

        if (playersError) throw playersError;
        const tournamentPlayers = tournamentPlayersData.map(tp => tp.player);
        setPlayers(tournamentPlayers);

        // Check if current user is signed up
        if (session) {
          const userPlayer = tournamentPlayers.find(player => {
            // Find player associated with this user
            return player.user_id === session.user.id;
          });
          setSignedUp(!!userPlayer);
        }

        // Create a map of player IDs to player objects for easy lookup
        const playerMapObj = {};
        tournamentPlayers.forEach(player => {
          playerMapObj[player.id] = player;
        });
        setPlayerMap(playerMapObj);

        // Fetch tournament matches
        const { data: matchesData, error: matchesError } = await supabase
          .from('tournament_matches')
          .select('*')
          .eq('tournament_id', id)
          .order('round', { ascending: true })
          .order('match_id', { ascending: true });

        if (matchesError) throw matchesError;
        setMatches(matchesData);

        // Process any matches with byes
        const updatedMatches = [...matchesData];
        const matchesWithByes = matchesData.filter(match => match.bye && !match.completed);
        
        for (const byeMatch of matchesWithByes) {
          const processedMatches = processAutomaticAdvancement(updatedMatches, byeMatch.match_id);
          
          // Update matches in database
          if (processedMatches !== updatedMatches) {
            const matchToUpdate = processedMatches.find(m => m.match_id === byeMatch.match_id);
            const nextMatch = processedMatches.find(m => m.match_id === byeMatch.next_match_id);
            
            // Update the bye match
            await supabase
              .from('tournament_matches')
              .update({
                winner_id: matchToUpdate.winner_id,
                completed: true,
                score: 'BYE'
              })
              .eq('id', matchToUpdate.id);
              
            // Update the next match with the advancing player
            if (nextMatch) {
              const updateData = {};
              if (byeMatch.match_id % 2 === 0) {
                updateData.player1_id = matchToUpdate.winner_id;
              } else {
                updateData.player2_id = matchToUpdate.winner_id;
              }
              
              await supabase
                .from('tournament_matches')
                .update(updateData)
                .eq('id', nextMatch.id);
            }
          }
        }
        
        // Fetch matches again if any were updated
        if (matchesWithByes.length > 0) {
          const { data: updatedMatchesData } = await supabase
            .from('tournament_matches')
            .select('*')
            .eq('tournament_id', id)
            .order('round', { ascending: true })
            .order('match_id', { ascending: true });
            
          if (updatedMatchesData) {
            setMatches(updatedMatchesData);
          }
        }

        // Check if tournament is complete and update status if needed
        const tournamentComplete = isTournamentCompleted(matchesData);
        if (tournamentComplete && tournamentData.status !== 'completed') {
          const winnerId = getTournamentWinner(matchesData);
          
          await supabase
            .from('tournaments')
            .update({
              status: 'completed',
              winner_id: winnerId
            })
            .eq('id', id);
            
          // Refresh tournament data
          const { data: refreshedTournament } = await supabase
            .from('tournaments')
            .select('*')
            .eq('id', id)
            .single();
            
          if (refreshedTournament) {
            setTournament(refreshedTournament);
          }
        }
        
        // Fetch all active players for sign-up functionality
        if (tournamentData.status === 'upcoming') {
          const { data: allPlayersData } = await supabase
            .from('players')
            .select('id, username, user_id, elo, avatar_url')
            .eq('is_active', true)
            .order('username');
          
          if (allPlayersData) {
            setAllPlayers(allPlayersData);
          }
        }
        
      } catch (err) {
        console.error('Error fetching tournament data:', err.message);
        setError('Failed to load tournament data');
      } finally {
        setLoading(false);
      }
    };

    fetchTournamentData();
  }, [id, supabase, session]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Get tournament status label and color
  const getTournamentStatus = (status) => {
    switch (status) {
      case 'upcoming':
        return { label: 'Upcoming', color: 'text-primary bg-primary/10' };
      case 'active':
        return { label: 'Active', color: 'text-success bg-success/10' };
      case 'completed':
        return { label: 'Completed', color: 'text-muted-foreground bg-muted' };
      default:
        return { label: 'Unknown', color: 'text-muted-foreground bg-muted' };
    }
  };

  // Navigate back
  const handleBack = () => {
    navigate('/tournaments');
  };

  // Activate tournament (change status from upcoming to active)
  const handleActivateTournament = async () => {
    if (!tournament || tournament.status !== 'upcoming') return;
    
    try {
      const { error } = await supabase
        .from('tournaments')
        .update({
          status: 'active',
          start_date: new Date().toISOString().split('T')[0]
        })
        .eq('id', id);
        
      if (error) throw error;
      
      // Refresh tournament data
      setTournament({
        ...tournament,
        status: 'active',
        start_date: new Date().toISOString().split('T')[0]
      });
      
    } catch (err) {
      console.error('Error activating tournament:', err.message);
      setError('Failed to activate tournament');
    }
  };

  // Open match result form
  const handleRecordMatch = (match) => {
    if (!match || match.completed) return;
    
    setActivatingMatch(match);
    setMatchFormData({
      player1Score: '',
      player2Score: ''
    });
    setShowMatchForm(true);
  };

  // Handle input changes in match form
  const handleMatchInputChange = (e) => {
    const { name, value } = e.target;
    // Only allow numeric values
    if (!/^\d*$/.test(value)) return;
    
    setMatchFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle submit match result
  const handleSubmitMatchResult = async (e) => {
    e.preventDefault();
    if (!activatingMatch || submitting) return;
    
    const player1Score = parseInt(matchFormData.player1Score) || 0;
    const player2Score = parseInt(matchFormData.player2Score) || 0;
    
    // Validate scores
    if (player1Score === player2Score) {
      setError('Scores cannot be tied. One player must win.');
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      // Determine winner and loser
      const winnerId = player1Score > player2Score ? activatingMatch.player1_id : activatingMatch.player2_id;
      const loserId = player1Score > player2Score ? activatingMatch.player2_id : activatingMatch.player1_id;
      
      // Update match with result
      const { error: updateError } = await supabase
        .from('tournament_matches')
        .update({
          winner_id: winnerId,
          loser_id: loserId,
          score: `${player1Score}-${player2Score}`,
          completed: true
        })
        .eq('id', activatingMatch.id);
        
      if (updateError) throw updateError;
      
      // Find the next match and update it with advancing player
      const nextMatchId = activatingMatch.next_match_id;
      if (nextMatchId) {
        const nextMatch = matches.find(m => m.match_id === nextMatchId);
        if (nextMatch) {
          const updateData = {};
          // Place the winner in the appropriate position based on match id (even/odd)
          if (activatingMatch.match_id % 2 === 0) {
            updateData.player1_id = winnerId;
          } else {
            updateData.player2_id = winnerId;
          }
          
          const { error: nextMatchError } = await supabase
            .from('tournament_matches')
            .update(updateData)
            .eq('id', nextMatch.id);
            
          if (nextMatchError) throw nextMatchError;
        }
      }
      
      // Also log the game in the regular games table
      if (session) {
        const { error: gameError } = await supabase
          .from('games')
          .insert([{
            team1_player1: winnerId,
            team1_score: winnerId === activatingMatch.player1_id ? player1Score : player2Score,
            team2_player1: loserId,
            team2_score: loserId === activatingMatch.player1_id ? player1Score : player2Score,
            logged_by: session.user.id,
            tournament_id: tournament.id,
            tournament_match_id: activatingMatch.id
          }]);
          
        if (gameError) {
          console.error('Error logging tournament game:', gameError);
          // Don't throw - we want to continue even if game logging fails
        }
      }
      
      // Refresh matches data
      const { data: refreshedMatches, error: matchesError } = await supabase
        .from('tournament_matches')
        .select('*')
        .eq('tournament_id', id)
        .order('round', { ascending: true })
        .order('match_id', { ascending: true });
        
      if (matchesError) throw matchesError;
      setMatches(refreshedMatches);
      
      // Check if tournament is now complete
      const tournamentComplete = isTournamentCompleted(refreshedMatches);
      if (tournamentComplete && tournament.status !== 'completed') {
        const winnerId = getTournamentWinner(refreshedMatches);
        
        await supabase
          .from('tournaments')
          .update({
            status: 'completed',
            winner_id: winnerId,
            end_date: new Date().toISOString().split('T')[0]
          })
          .eq('id', id);
          
        // Refresh tournament data
        const { data: refreshedTournament } = await supabase
          .from('tournaments')
          .select('*')
          .eq('id', id)
          .single();
          
        if (refreshedTournament) {
          setTournament(refreshedTournament);
        }
      }
      
      // Reset state
      setShowMatchForm(false);
      setActivatingMatch(null);
      
    } catch (err) {
      console.error('Error submitting match result:', err.message);
      setError('Failed to record match result');
    } finally {
      setSubmitting(false);
    }
  };

  // Get player name by ID
  const getPlayerName = (playerId) => {
    return playerMap[playerId]?.username || 'Unknown Player';
  };

  // Render tournament bracket
  const renderBracket = () => {
    if (!matches || matches.length === 0) return null;
    
    // Group matches by round
    const roundsMap = {};
    matches.forEach(match => {
      if (!roundsMap[match.round]) {
        roundsMap[match.round] = [];
      }
      roundsMap[match.round].push(match);
    });
    
    const rounds = Object.keys(roundsMap).map(Number).sort((a, b) => a - b);
    const maxRound = Math.max(...rounds);
    
    return (
      <div className="mt-6 overflow-x-auto bg-card rounded-md p-4">
        <div className="flex gap-4 pb-4 min-w-[800px]">
          {rounds.map(round => (
            <div 
              key={round}
              className={cn(
                "flex flex-col gap-4",
                // Make the final match wider
                round === maxRound ? "min-w-[240px]" : "min-w-[200px]"
              )}
            >
              <div className="text-center font-medium text-gray-700 pb-2">
                {round === maxRound ? 'Final' : round === maxRound - 1 ? 'Semifinals' : `Round ${round}`}
              </div>
              
              <div className={cn(
                "flex flex-col gap-4",
                // Add appropriate spacing to align matches visually
                round > 1 && `mt-${Math.pow(2, round-1) * 4}`,
                round > 1 && `gap-${Math.pow(2, round) * 4}`
              )}>
                {roundsMap[round].map(match => (
                  <Card 
                    key={match.id} 
                    className={cn(
                      "border overflow-hidden",
                      match.completed ? "border-muted" : "border-input",
                      (!match.completed && match.player1_id && match.player2_id && tournament?.status === 'active') && 
                        "hover:border-primary cursor-pointer"
                    )}
                    onClick={() => (!match.completed && match.player1_id && match.player2_id && tournament?.status === 'active') && 
                      handleRecordMatch(match)}
                  >
                    <div className="flex flex-col">
                      {/* Player 1 */}
                      <div className={cn(
                        "flex items-center p-2 border-b",
                        match.completed && match.winner_id === match.player1_id ? "bg-success/10" : "",
                        !match.player1_id ? "opacity-50" : ""
                      )}>
                        {match.player1_id ? (
                          <>
                            <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center text-xs font-medium mr-2">
                              {getPlayerName(match.player1_id).charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 truncate">{getPlayerName(match.player1_id)}</div>
                            {match.completed && (
                              <div className="ml-auto font-medium">
                                {match.score ? match.score.split('-')[0] : ''}
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="text-muted-foreground text-sm">TBD</div>
                        )}
                      </div>
                      
                      {/* Player 2 */}
                      <div className={cn(
                        "flex items-center p-2",
                        match.completed && match.winner_id === match.player2_id ? "bg-success/10" : "",
                        !match.player2_id ? "opacity-50" : ""
                      )}>
                        {match.player2_id ? (
                          <>
                            <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center text-xs font-medium mr-2">
                              {getPlayerName(match.player2_id).charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 truncate">{getPlayerName(match.player2_id)}</div>
                            {match.completed && (
                              <div className="ml-auto font-medium">
                                {match.score ? match.score.split('-')[1] : ''}
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="text-muted-foreground text-sm">TBD</div>
                        )}
                      </div>
                      
                      {/* Match status indicator */}
                      {match.completed ? (
                        match.score === 'BYE' ? (
                          <div className="px-2 py-1 bg-muted text-muted-foreground text-xs text-center">
                            Bye - {getPlayerName(match.winner_id)} advances
                          </div>
                        ) : (
                          <div className="px-2 py-1 bg-success/10 text-success text-xs text-center">
                            Completed
                          </div>
                        )
                      ) : (match.player1_id && match.player2_id && tournament?.status === 'active') ? (
                        <div className="px-2 py-1 bg-primary/10 text-primary text-xs text-center">
                          Ready to play
                        </div>
                      ) : (
                        <div className="px-2 py-1 bg-muted text-muted-foreground text-xs text-center">
                          Waiting
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // New functions for player sign-up and tournament management
  
  // Handle player sign-up
  const handleSignup = async () => {
    if (!session) {
      setError('You must be logged in to sign up for a tournament');
      return;
    }
    
    try {
      // Find player associated with current user
      const { data: userPlayers } = await supabase
        .from('players')
        .select('id')
        .eq('user_id', session.user.id)
        .eq('is_active', true);
      
      if (!userPlayers || userPlayers.length === 0) {
        setError('You need to create a player profile first');
        return;
      }
      
      const playerId = userPlayers[0].id;
      
      // Add player to tournament
      const { error } = await supabase
        .from('tournament_players')
        .insert([{
          tournament_id: id,
          player_id: playerId
        }]);
      
      if (error) throw error;
      
      // Update tournament player count
      await supabase
        .from('tournaments')
        .update({
          player_count: tournament.player_count + 1
        })
        .eq('id', id);
      
      // Refresh page data
      setSignedUp(true);
      setTournament({
        ...tournament,
        player_count: tournament.player_count + 1
      });
      
      const { data: updatedPlayers } = await supabase
        .from('tournament_players')
        .select('*, player:player_id(*)')
        .eq('tournament_id', id);
      
      if (updatedPlayers) {
        setPlayers(updatedPlayers.map(tp => tp.player));
      }
      
      setShowSignupDialog(false);
    } catch (err) {
      console.error('Error signing up for tournament:', err.message);
      setError('Failed to sign up for tournament');
    }
  };
  
  // Handle opening matchup manager
  const handleOpenMatchupManager = () => {
    if (tournament && tournament.status === 'upcoming' && isAdmin) {
      // Initialize selectedPlayersForMatches with player IDs
      const initialSelections = {};
      players.forEach(player => {
        initialSelections[player.id] = null;
      });
      setSelectedPlayersForMatches(initialSelections);
      
      // Create initial empty match pairs
      // Calculate number of matches needed for first round
      const numPlayers = players.length;
      const numMatches = Math.ceil(numPlayers / 2);
      const initialPairs = Array(numMatches).fill().map(() => ({ player1: null, player2: null }));
      setMatchPairs(initialPairs);
      
      setShowMatchupManager(true);
    }
  };
  
  // Handle assigning player to a match position
  const handleAssignPlayer = (matchIndex, position, playerId) => {
    // Update match pairs
    const updatedPairs = [...matchPairs];
    
    // If player already assigned elsewhere, remove that assignment
    updatedPairs.forEach((pair, idx) => {
      if (pair.player1 === playerId) updatedPairs[idx].player1 = null;
      if (pair.player2 === playerId) updatedPairs[idx].player2 = null;
    });
    
    // Assign player to the new position
    updatedPairs[matchIndex][position] = playerId;
    setMatchPairs(updatedPairs);
  };
  
  // Save matchups and generate bracket
  const handleSaveMatchups = async () => {
    try {
      // Validate that all players are assigned
      const assignedPlayers = matchPairs.flatMap(pair => [pair.player1, pair.player2].filter(Boolean));
      const uniqueAssigned = [...new Set(assignedPlayers)];
      
      if (uniqueAssigned.length !== players.length) {
        setError('Not all players have been assigned to matches');
        return;
      }
      
      // Delete existing matches if any
      if (matches.length > 0) {
        await supabase
          .from('tournament_matches')
          .delete()
          .eq('tournament_id', id);
      }
      
      // Generate custom bracket based on matchups
      const customMatches = [];
      const bracketSize = Math.pow(2, Math.ceil(Math.log2(players.length)));
      
      // First round matches with custom matchups
      for (let i = 0; i < matchPairs.length; i++) {
        const pair = matchPairs[i];
        customMatches.push({
          tournament_id: id,
          match_id: i + 1,
          round: 1,
          player1_id: pair.player1,
          player2_id: pair.player2,
          winner_id: null,
          loser_id: null,
          score: null,
          completed: false,
          next_match_id: Math.floor(i / 2) + Math.floor(bracketSize / 4) + 1,
          bye: !pair.player1 || !pair.player2
        });
      }
      
      // Create placeholder matches for subsequent rounds
      let currentRound = 2;
      let matchId = Math.floor(bracketSize / 2) + 1;
      let remainingMatches = Math.floor(bracketSize / 4);
      
      while (remainingMatches > 0) {
        for (let i = 0; i < remainingMatches; i++) {
          customMatches.push({
            tournament_id: id,
            match_id: matchId,
            round: currentRound,
            player1_id: null,
            player2_id: null,
            winner_id: null,
            loser_id: null,
            score: null,
            completed: false,
            next_match_id: remainingMatches > 1 ? Math.floor(i / 2) + matchId + remainingMatches : null,
            bye: false
          });
          matchId++;
        }
        
        currentRound++;
        remainingMatches = Math.floor(remainingMatches / 2);
      }
      
      // Insert the custom matches
      const { error } = await supabase
        .from('tournament_matches')
        .insert(customMatches);
        
      if (error) throw error;
      
      // Refresh matches
      const { data: updatedMatches } = await supabase
        .from('tournament_matches')
        .select('*')
        .eq('tournament_id', id)
        .order('round', { ascending: true })
        .order('match_id', { ascending: true });
        
      if (updatedMatches) {
        setMatches(updatedMatches);
      }
      
      setShowMatchupManager(false);
    } catch (err) {
      console.error('Error saving matchups:', err.message);
      setError('Failed to save matchups');
    }
  };
  
  // Close registration and lock in players
  const handleCloseRegistration = async () => {
    if (!isAdmin || tournament.status !== 'upcoming') return;
    
    try {
      await supabase
        .from('tournaments')
        .update({
          registration_closed: true
        })
        .eq('id', id);
        
      setRegistrationOpen(false);
      setTournament({
        ...tournament,
        registration_closed: true
      });
    } catch (err) {
      console.error('Error closing registration:', err.message);
      setError('Failed to close registration');
    }
  };
  
  // Start the tournament with current matchups
  const handleStartTournament = async () => {
    if (!isAdmin || tournament.status !== 'upcoming') return;
    
    try {
      // Check if matches exist
      if (matches.length === 0) {
        setError('You need to set up matches before starting the tournament');
        return;
      }
      
      // Update tournament status
      const { error } = await supabase
        .from('tournaments')
        .update({
          status: 'active',
          start_date: new Date().toISOString().split('T')[0],
          registration_closed: true
        })
        .eq('id', id);
        
      if (error) throw error;
      
      // Refresh tournament data
      setTournament({
        ...tournament,
        status: 'active',
        start_date: new Date().toISOString().split('T')[0],
        registration_closed: true
      });
      
      setRegistrationOpen(false);
    } catch (err) {
      console.error('Error starting tournament:', err.message);
      setError('Failed to start tournament');
    }
  };

  // Handle player withdrawal
  const handleWithdraw = async () => {
    if (!session || !signedUp) {
      return;
    }
    
    try {
      // Find player associated with current user
      const { data: userPlayers } = await supabase
        .from('players')
        .select('id')
        .eq('user_id', session.user.id)
        .eq('is_active', true);
      
      if (!userPlayers || userPlayers.length === 0) {
        setError('Player profile not found');
        return;
      }
      
      const playerId = userPlayers[0].id;
      
      // Remove player from tournament
      const { error } = await supabase
        .from('tournament_players')
        .delete()
        .eq('tournament_id', id)
        .eq('player_id', playerId);
      
      if (error) throw error;
      
      // Update tournament player count
      if (tournament.player_count > 0) {
        await supabase
          .from('tournaments')
          .update({
            player_count: tournament.player_count - 1
          })
          .eq('id', id);
      }
      
      // Refresh page data
      setSignedUp(false);
      setTournament({
        ...tournament,
        player_count: Math.max(0, tournament.player_count - 1)
      });
      
      // Refresh player list
      const { data: updatedPlayers } = await supabase
        .from('tournament_players')
        .select('*, player:player_id(*)')
        .eq('tournament_id', id);
      
      if (updatedPlayers) {
        setPlayers(updatedPlayers.map(tp => tp.player));
      }
      
    } catch (err) {
      console.error('Error withdrawing from tournament:', err.message);
      setError('Failed to withdraw from tournament');
    }
  };

  // Render function with new UI elements
  return (
    <div className={cn(
      "flex flex-col w-full p-4 md:p-6 gap-6 pb-20",
      isPwa && "pt-12" // Add padding for PWA status bar
    )}>
      {/* Tournament Header */}
      <div className="flex items-center gap-2">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={handleBack}
          className="h-8 w-8"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{tournament?.name || 'Tournament'}</h1>
        </div>
        
        {/* Tournament Actions */}
        {tournament && session && (
          <div className="flex gap-2">
            {/* Player Sign-up Button */}
            {tournament.status === 'upcoming' && !signedUp && !isAdmin && (
              <Button onClick={() => setShowSignupDialog(true)} className="flex items-center gap-2">
                <UserPlus className="w-4 h-4" />
                Sign Up
              </Button>
            )}
            
            {/* Player Withdraw Button */}
            {tournament.status === 'upcoming' && signedUp && !isAdmin && (
              <Button 
                variant="outline" 
                onClick={handleWithdraw} 
                className="flex items-center gap-2 border-red-200 hover:bg-red-50 text-red-600"
              >
                <X className="w-4 h-4" />
                Withdraw
              </Button>
            )}
            
            {/* Admin Actions */}
            {isAdmin && tournament.status === 'upcoming' && (
              <>
                <Button 
                  variant="outline" 
                  onClick={handleCloseRegistration}
                  disabled={!registrationOpen}
                >
                  Close Registration
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleOpenMatchupManager}
                >
                  Set Matchups
                </Button>
                <Button 
                  onClick={handleStartTournament}
                  className="flex items-center gap-2"
                >
                  <Trophy className="w-4 h-4" />
                  Start Tournament
                </Button>
              </>
            )}
          </div>
        )}
      </div>
      
      {/* Content */}
      {loading ? (
        <div className="flex flex-col items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <p className="mt-4 text-gray-600">Loading tournament data...</p>
        </div>
      ) : error ? (
        <div className="text-red-500 p-4 bg-red-50 rounded-md mb-4">{error}</div>
      ) : tournament ? (
        <>
          {/* Registration Status */}
          {tournament.status === 'upcoming' && (
            <Alert variant={registrationOpen ? "default" : "destructive"} className="mb-4">
              <AlertDescription>
                {registrationOpen 
                  ? "Registration is open! Players can sign up for this tournament." 
                  : "Registration is closed. No new players can join."}
              </AlertDescription>
            </Alert>
          )}
          
          <Tabs defaultValue="bracket" className="w-full">
            <TabsList>
              <TabsTrigger value="bracket">Bracket</TabsTrigger>
              <TabsTrigger value="players">Players ({players.length || 0})</TabsTrigger>
              {tournament.status === 'active' && (
                <TabsTrigger value="matches">Upcoming Matches</TabsTrigger>
              )}
            </TabsList>
            
            <TabsContent value="bracket" className="pt-4">
              {renderBracket()}
              {(!matches || matches.length === 0) && (
                <Card className="p-8 text-center">
                  <CardContent>
                    <Trophy className="w-12 h-12 mx-auto text-muted-foreground my-4" />
                    <CardTitle className="text-xl mb-2">No bracket yet</CardTitle>
                    <CardDescription>
                      {isAdmin && tournament.status === 'upcoming' 
                        ? "Set up the tournament matchups to create the bracket." 
                        : "The tournament bracket hasn't been created yet."}
                    </CardDescription>
                    
                    {isAdmin && tournament.status === 'upcoming' && (
                      <Button 
                        onClick={handleOpenMatchupManager} 
                        className="mt-4"
                      >
                        Set Matchups
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>
            
            <TabsContent value="players" className="pt-4">
              <Card>
                <CardHeader>
                  <CardTitle>Tournament Players</CardTitle>
                  <CardDescription>
                    {tournament.status === 'upcoming' && registrationOpen
                      ? 'Players currently registered for this tournament.'
                      : `${players.length} players participating in this tournament.`}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {players.map(player => (
                      <div key={player.id} className="flex items-center gap-2 p-2 border rounded-md">
                        <Avatar className="h-8 w-8">
                          {player.avatar_url ? (
                            <AvatarImage src={player.avatar_url} alt={player.username} />
                          ) : (
                            <AvatarFallback>{player.username.slice(0, 2).toUpperCase()}</AvatarFallback>
                          )}
                        </Avatar>
                        <div className="flex-1">
                          <p className="font-medium">{player.username}</p>
                          <p className="text-sm text-muted-foreground">Rating: {player.elo || 1000}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            {tournament.status === 'active' && (
              <TabsContent value="matches" className="pt-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Upcoming Matches</CardTitle>
                    <CardDescription>Matches that are ready to be played</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {matches
                        .filter(match => 
                          !match.completed && 
                          match.player1_id && 
                          match.player2_id
                        )
                        .map(match => (
                          <Card key={match.id} className="overflow-hidden">
                            <CardContent className="p-4">
                              <div className="flex justify-between items-center">
                                <div className="flex items-center gap-2">
                                  <Avatar className="h-8 w-8">
                                    <AvatarFallback>
                                      {getPlayerName(match.player1_id)?.slice(0, 2).toUpperCase()}
                                    </AvatarFallback>
                                  </Avatar>
                                  <span className="font-medium">{getPlayerName(match.player1_id)}</span>
                                </div>
                                <span className="text-lg font-bold mx-4">vs</span>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{getPlayerName(match.player2_id)}</span>
                                  <Avatar className="h-8 w-8">
                                    <AvatarFallback>
                                      {getPlayerName(match.player2_id)?.slice(0, 2).toUpperCase()}
                                    </AvatarFallback>
                                  </Avatar>
                                </div>
                              </div>
                              <div className="mt-4 flex justify-center">
                                <Button 
                                  onClick={() => handleRecordMatch(match)}
                                  className="flex items-center gap-2"
                                >
                                  Record Result
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      
                      {matches.filter(match => 
                        !match.completed && 
                        match.player1_id && 
                        match.player2_id
                      ).length === 0 && (
                        <p className="text-center text-muted-foreground p-4">
                          No upcoming matches available right now.
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            )}
          </Tabs>
        </>
      ) : null}
      
      {/* Tournament Signup Dialog */}
      <Dialog open={showSignupDialog} onOpenChange={setShowSignupDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sign Up for Tournament</DialogTitle>
            <DialogDescription>
              Are you sure you want to sign up for {tournament?.name}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignupDialog(false)}>Cancel</Button>
            <Button onClick={handleSignup}>Sign Up</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Match-up Manager Dialog */}
      <Dialog open={showMatchupManager} onOpenChange={setShowMatchupManager}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>Create Tournament Matchups</DialogTitle>
            <DialogDescription>
              Assign players to initial matches to set up the tournament bracket.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="grid grid-cols-1 gap-6">
              {matchPairs.map((pair, index) => (
                <div key={index} className="border p-4 rounded-md">
                  <h3 className="font-medium mb-2">Match {index + 1}</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Player 1</Label>
                      <Select 
                        value={pair.player1 ? pair.player1 : "bye"}
                        onValueChange={(value) => handleAssignPlayer(index, 'player1', value === "bye" ? null : value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a player" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="bye">None (Bye)</SelectItem>
                          {players.map(player => (
                            <SelectItem key={player.id} value={player.id}>
                              {player.username}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Player 2</Label>
                      <Select 
                        value={pair.player2 ? pair.player2 : "bye"}
                        onValueChange={(value) => handleAssignPlayer(index, 'player2', value === "bye" ? null : value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a player" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="bye">None (Bye)</SelectItem>
                          {players.map(player => (
                            <SelectItem key={player.id} value={player.id}>
                              {player.username}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMatchupManager(false)}>Cancel</Button>
            <Button onClick={handleSaveMatchups}>Save Matchups</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Match Result Form Dialog */}
      <Dialog open={showMatchForm} onOpenChange={setShowMatchForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Match Result</DialogTitle>
            <DialogDescription>
              {activatingMatch ? 
                `${getPlayerName(activatingMatch.player1_id)} vs ${getPlayerName(activatingMatch.player2_id)}`
                : 'Loading match details...'
              }
            </DialogDescription>
          </DialogHeader>
          
          {/* Add the form here */}
          {activatingMatch && (
            <form onSubmit={handleSubmitMatchResult} className="space-y-4 py-3">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="player1Score">
                    {getPlayerName(activatingMatch.player1_id)} Score
                  </Label>
                  <Input
                    id="player1Score"
                    name="player1Score"
                    type="number"
                    min="0"
                    value={matchFormData.player1Score}
                    onChange={handleMatchInputChange}
                    placeholder="0"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="player2Score">
                    {getPlayerName(activatingMatch.player2_id)} Score
                  </Label>
                  <Input
                    id="player2Score"
                    name="player2Score"
                    type="number"
                    min="0"
                    value={matchFormData.player2Score}
                    onChange={handleMatchInputChange}
                    placeholder="0"
                    required
                  />
                </div>
              </div>
              
              {error && (
                <div className="text-red-500 text-sm">{error}</div>
              )}
            </form>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowMatchForm(false);
              setActivatingMatch(null);
              setError(null);
            }}>Cancel</Button>
            <Button onClick={handleSubmitMatchResult} disabled={!activatingMatch || submitting}>
              {submitting ? 'Saving...' : 'Save Result'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TournamentDetails; 